import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../@core/auth/jwt/jwtService";

export const getAttendeesByNo = createAsyncThunk(
  "attendees/getAttendeesByNo",
  async (args, thunkAPI) => {
    const { eventId, number } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/find`,
      method: "get",
      params: {
        number,
      },
    });
    return { data: response.data };
  }
);

export const getAttendees = createAsyncThunk(
  "attendees/getAttendees",
  async (args, thunkAPI) => {
    const { eventId, searchQuery, checkin, certificate, kit, subscriptionId } =
      args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees`,
      method: "get",
      params: {
        subscriptionId,
        searchQuery,
        certificate,
        checkin,
        kit,
      },
    });
    return { data: response.data, headers: response.headers };
  }
);

export const getAttendeesToExport = createAsyncThunk(
  "attendees/getAttendeesToExport",
  async (eventId, thunkAPI) => {
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/export`,
      method: "get",
    });
    return { data: response.data };
  }
);

export const deliverKit = createAsyncThunk(
  "attendees/deliverKit",
  async (args, thunkAPI) => {
    const { eventId, data, id } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${id}/assignKit`,
      method: "post",
      data,
    });
    return { data: response };
  }
);

export const updateAttendee = createAsyncThunk(
  "attendees/updateAttendee",
  async (args, thunkAPI) => {
    const { eventId, data, id } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${id}`,
      method: "patch",
      data,
    });
    return { data: response };
  }
);

export const getMoreAttendees = createAsyncThunk(
  "attendees/getMoreAttendees",
  async (args, thunkAPI) => {
    const {
      kit,
      checkin,
      eventId,
      searchQuery,
      certificate,
      xContinuation,
      subscriptionId,
    } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees`,
      method: "get",
      headers: { ["x-continuation-token"]: xContinuation },
      params: {
        subscriptionId,
        searchQuery,
        certificate,
        checkin,
        kit,
      },
    });
    return { data: response.data, headers: response.headers };
  }
);

export const doCheckIn = async (args) => {
  const { id, eventId } = args;
  await axiosInstance.post(`/events/${eventId}/attendees/${id}/checkin`, "");
};

export const getBadge = createAsyncThunk(
  "attendees/getBadge",
  async (args, thunkAPI) => {
    const { attendeeId, eventId, xContinuation } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${attendeeId}/printBadge`,
      headers: { ["x-continuation-token"]: xContinuation },
      method: "get",
    });
    return { data: response.data };
  }
);

export const getBadgePDF = createAsyncThunk(
  "attendees/getBadgePDF",
  async (args, thunkAPI) => {
    const { attendeeId, eventId } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${attendeeId}/printBadge?format=print`,
      method: "get",
      responseType: "blob",
    });
    const pdfFile = new Blob([response.data], { type: "application/pdf" });
    const urlFile = URL.createObjectURL(pdfFile);
    const iframe = document.createElement("iframe"); //load content in an iframe to print later
    document.body.appendChild(iframe);

    iframe.style.display = "none";
    iframe.src = urlFile;
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow.print();
      }, 1);
    };
    // ? print with print-js. don't work with system dialog
    // printJS({
    //   printable: urlFile,
    //   style: 'margin: 0; padding: 0;',
    // });
    return { data: response.data };
  }
);

export const getCertificate = createAsyncThunk(
  "attendees/getCertificate",
  async (args, thunkAPI) => {
    const { attendeeId, eventId, xContinuation } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${attendeeId}/certificate`,
      headers: { ["x-continuation-token"]: xContinuation },
      method: "get",
    });
    return { data: response.data };
  }
);

export const getCertificatePDF = createAsyncThunk(
  "attendees/getCertificatePDF",
  async (args, thunkAPI) => {
    const { attendeeId, eventId } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${attendeeId}/certificate?format=print`,
      method: "get",
      responseType: "blob",
    });
    const pdfFile = new Blob([response.data], { type: "application/pdf" });
    const urlFile = URL.createObjectURL(pdfFile);
    const iframe = document.createElement("iframe"); //load content in an iframe to print later
    document.body.appendChild(iframe);

    iframe.style.display = "none";
    iframe.src = urlFile;
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow.print();
      }, 1);
    };
    return { data: response.data };
  }
);

export const downloadCertificate = createAsyncThunk(
  "attendees/downloadCertificate",
  async (args) => {
    const { attendeeId, eventId, name, format } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${attendeeId}/certificate`,
      method: "get",
      params: { format },
      responseType: "blob",
    });
    const urlFile = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.setAttribute("href", urlFile);
    link.setAttribute("download", `${name}.${format}`);
    document.body.appendChild(link);
    link.click();
  }
);

export const sendCertificate = createAsyncThunk(
  "attendees/getCertificate",
  async (args, thunkAPI) => {
    const { attendeeId, eventId, email } = args;
    const response = await axiosInstance.request({
      url: `/events/${eventId}/attendees/${attendeeId}/certificate/send`,
      method: "post",
      params: { email },
    });
    return { data: response.data };
  }
);

export const attendeeSlice = createSlice({
  name: "attendees",
  initialState: {
    totalRows: 0,
    attendees: [],
    attendee: {},
    loader: false,
    hasMore: false,
    inputSearch: "",
    loaderPDF: false,
    loaderKit: false,
    xContinuation: "",
    loaderCPDF: false,
    attendeesLength: 0,
    loaderExport: false,
    loaderSender: false,
    loaderUpdate: false,
    loaderAttendee: false,
    downloadLoader: false,
    attendeesToExport: [],
    attendeesToSelect: [],
    loaderConstancy: false,
    attendeesLoader: false,
    attendeesToDownload: [],
  },
  reducers: {
    setEventList: (state, action) => {
      state.eventsArray = action.payload;
    },
    setInputSearch: (state, action) => {
      state.inputSearch = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAttendees.fulfilled, (state, action) => {
        state.attendees = action.payload.data.data;
        state.attendeesToSelect = action?.payload?.data?.data;
        state.attendeesToDownload = action.payload.data.data;
        state.attendeesLength = action.payload?.data?.data?.length;
        state.attendeesLoader = false;
        state.xContinuation = action.payload.headers["x-continuation-token"];
        state.totalRows = action.payload?.data?.total;
        state.hasMore = action.payload?.data?.hasNext;
      })
      .addCase(getAttendees.pending, (state, action) => {
        state.attendeesLoader = true;
      })
      .addCase(getMoreAttendees.fulfilled, (state, action) => {
        state.attendees = [...state.attendees, ...action.payload.data.data];
        state.attendeesLength = action.payload?.data?.data?.length;
        state.xContinuation = action.payload.headers["x-continuation-token"];
        state.hasMore = action.payload?.data?.hasNext;
      })
      .addCase(getMoreAttendees.pending, (state, action) => {})
      .addCase(getBadgePDF.fulfilled, (state, action) => {
        state.loaderPDF = false;
      })
      .addCase(getBadgePDF.pending, (state, action) => {
        state.loaderPDF = true;
      })
      .addCase(getBadgePDF.rejected, (state, action) => {
        state.loaderPDF = false;
      })
      .addCase(getBadge.fulfilled, (state, action) => {
        state.loader = false;
      })
      .addCase(getBadge.pending, (state, action) => {
        state.loader = true;
      })
      .addCase(getBadge.rejected, (state, action) => {
        state.loader = false;
      })
      .addCase(downloadCertificate.fulfilled, (state, action) => {
        state.downloadLoader = false;
      })
      .addCase(downloadCertificate.pending, (state, action) => {
        state.downloadLoader = true;
      })
      .addCase(downloadCertificate.rejected, (state, action) => {
        state.downloadLoader = false;
      })
      .addCase(getCertificatePDF.fulfilled, (state, action) => {
        state.loaderCPDF = false;
      })
      .addCase(getCertificatePDF.pending, (state, action) => {
        state.loaderCPDF = true;
      })
      .addCase(getCertificatePDF.rejected, (state, action) => {
        state.loaderCPDF = false;
      })
      .addCase(sendCertificate.fulfilled, (state, action) => {
        state.loaderSender = false;
      })
      .addCase(sendCertificate.pending, (state, action) => {
        state.loaderSender = true;
      })
      .addCase(sendCertificate.rejected, (state, action) => {
        state.loaderSender = false;
      })
      .addCase(deliverKit.fulfilled, (state, action) => {
        state.loaderKit = false;
      })
      .addCase(deliverKit.pending, (state, action) => {
        state.loaderKit = true;
      })
      .addCase(deliverKit.rejected, (state, action) => {
        state.loaderKit = false;
      })
      .addCase(updateAttendee.fulfilled, (state, action) => {
        state.loaderConstancy = false;
        state.loaderUpdate = false;
      })
      .addCase(updateAttendee.pending, (state, action) => {
        state.loaderConstancy = true;
        state.loaderUpdate = true;
      })
      .addCase(updateAttendee.rejected, (state, action) => {
        state.loaderConstancy = false;
        state.loaderUpdate = false;
      })
      .addCase(getAttendeesByNo.fulfilled, (state, action) => {
        state.attendee = action.payload.data;
        state.loaderAttendee = false;
      })
      .addCase(getAttendeesByNo.pending, (state, action) => {
        state.loaderAttendee = true;
      })
      .addCase(getAttendeesByNo.rejected, (state, action) => {
        state.loaderAttendee = false;
      })
      .addCase(getAttendeesToExport.fulfilled, (state, action) => {
        state.attendeesToExport = action.payload.data;
        state.loaderExport = false;
      })
      .addCase(getAttendeesToExport.pending, (state, action) => {
        state.loaderExport = true;
      })
      .addCase(getAttendeesToExport.rejected, (state, action) => {
        state.loaderExport = false;
      });
  },
});

export const { setInputSearch } = attendeeSlice.actions;

export default attendeeSlice.reducer;
